import { HeartbeatData } from '@kentik/ui-shared/types/agentHeartbeat.types';

export interface HeartbeatState {
  isAgentHeartbeatAlive: boolean;
  /** The turn the live heartbeat comes from, which a stop request names. */
  agentHeartbeatTurnId: string | null;
  isThinking?: boolean;
}

type SetHeartbeatState = (state: HeartbeatState) => void;

/**
 * Tracks the freshness of server-sent agent heartbeats on the client. The
 * server publishes `agent_heartbeat` events carrying a TTL; if a fresh
 * heartbeat does not arrive before the TTL elapses the agent is considered no
 * longer working. State changes are pushed back to the owner via the
 * `setState` callback (typically a mobx model's `set`).
 */
export default class AgentHeartbeatTracker {
  private timer: ReturnType<typeof setTimeout> | null = null;

  private readonly setState: SetHeartbeatState;

  constructor(setState: SetHeartbeatState) {
    this.setState = setState;
  }

  handle(data?: HeartbeatData): void {
    this.clear();

    const ttlMs = data?.ttl_ms ?? 0;
    const alive = data?.alive === true && ttlMs > 0;

    if (alive) {
      this.setState({ isAgentHeartbeatAlive: true, agentHeartbeatTurnId: data?.agent_turn_id ?? null });
      this.timer = setTimeout(() => {
        this.setState({ isAgentHeartbeatAlive: false, agentHeartbeatTurnId: null, isThinking: false });
      }, ttlMs);
    } else {
      this.setState({ isAgentHeartbeatAlive: false, agentHeartbeatTurnId: null, isThinking: false });
    }
  }

  clear(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
